// AM dashboard sections. Presentational: the page owns the queries and hands each
// section a PanelState, so one slow or failed panel only affects its own card.
// Staff look (the current app style: bordered cards, slate text), not the Kyck
// Design System. Every CPL goes through the ONE "spending, no leads" rule
// (advertisingHome/noLeads.ts): "No leads" in red with the spend, never $0.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, Play } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { cplCellText } from '../../lib/advertisingHome/noLeads';
import type { HomeAlert } from '../../lib/advertisingHome/alerts';
import type { AlertNote } from '../../types/advertisingHome';
import type {
  AmAssets,
  AmCampaignItem,
  AmCampaigns,
  AmPeople,
  AmTopAds,
} from '../../types/amDashboard';
import type { HighCplRow } from '../../lib/amDashboard/highCpl';
import type { MoverRow, MoversResult } from '../../lib/amDashboard/movers';
import type { GlanceRow } from '../../lib/amDashboard/glance';
import type { PaceResult, PacePeriod, PaceStatus, PaceTab } from '../../lib/amDashboard/pace';
import { viewAllAssetsHref } from '../../lib/amDashboard/home';
import { relativeTime } from '../../lib/amDashboard/relativeTime';
import { AlertTypeBadge, ChannelChip, SeverityDot } from '../advertisingHome/AlertBadges';

export interface PanelState<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
}

const money = (n: number, c: string): string => formatReportingAmount(n, c);
const pct = (r: number | null): string => (r === null ? '-' : `${Math.round(r * 100)}%`);

/** A card that owns its loading and error states. */
export function Panel<T>({
  id,
  title,
  state,
  right,
  children,
}: {
  id: string;
  title: string;
  state: PanelState<T>;
  right?: React.ReactNode;
  children: (data: T) => React.ReactNode;
}): React.JSX.Element {
  return (
    <section id={id} className="scroll-mt-4 rounded-lg border border-border bg-card p-4" data-testid={`panel-${id}`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {right}
      </div>
      {state.isError ? (
        <p className="text-sm text-red-700">
          This panel could not load.{' '}
          <button type="button" className="underline" onClick={() => void state.refetch()}>
            Try again
          </button>
        </p>
      ) : state.isLoading || state.data === undefined ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        children(state.data)
      )}
    </section>
  );
}

const Empty = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

const NoLeads = ({ spend, currency }: { spend: number; currency: string }): React.JSX.Element => (
  <span className="font-medium text-red-700" data-testid="no-leads">
    No leads <span className="text-xs font-normal">({money(spend, currency)} spent)</span>
  </span>
);

// ── 2 · Needs you today ──────────────────────────────────────────────────

export interface NeedTile {
  key: string;
  label: string;
  count: number | null;
  target: string;
  tone: 'red' | 'amber' | 'neutral';
}

export function NeedsTiles({ tiles, onJump }: { tiles: NeedTile[]; onJump: (t: NeedTile) => void }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="needs-tiles">
      {tiles.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onJump(t)}
          className={cn(
            'rounded-lg border p-3 text-left transition hover:shadow-sm',
            t.count === null || t.count === 0
              ? 'border-border bg-card'
              : t.tone === 'red'
                ? 'border-red-200 bg-red-50'
                : t.tone === 'amber'
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-border bg-card',
          )}
        >
          <span className="block text-2xl font-semibold tabular-nums">{t.count ?? '-'}</span>
          <span className="block text-xs text-muted-foreground">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── 3 · Alerts and team notes ────────────────────────────────────────────

export function AlertsAndNotes({
  alerts,
  notes,
  nameOf,
  onAck,
  pending,
}: {
  alerts: HomeAlert[];
  notes: AlertNote[];
  nameOf: (id: string) => string;
  onAck: (a: HomeAlert) => void;
  pending: boolean;
}): React.JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Alerts</h3>
        {alerts.length === 0 ? (
          <Empty>Nothing outstanding on your accounts.</Empty>
        ) : (
          <ul className="scroll-subtle max-h-72 space-y-2 overflow-y-auto pr-1">
            {alerts.slice(0, 30).map((a) => (
              <li key={`${a.accountId}${a.channel}${a.type}`} className="flex items-center gap-2 text-sm" data-testid="am-alert">
                <SeverityDot severity={a.severity} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{a.accountName}</span>{' '}
                  <ChannelChip channel={a.channel} /> <AlertTypeBadge type={a.type} />
                  <span className="block truncate text-xs text-muted-foreground">
                    {a.reason} · {a.figure}
                  </span>
                </span>
                <Link to={`/accounts/${a.accountId}`} className="text-xs underline">
                  Open
                </Link>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => onAck(a)}
                  className="rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
                >
                  Acknowledge
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Team notes (Meta and Google teams)
        </h3>
        {notes.length === 0 ? (
          <Empty>No notes from the ad teams on your accounts.</Empty>
        ) : (
          <ul className="scroll-subtle max-h-72 space-y-2 overflow-y-auto pr-1">
            {notes.slice(0, 15).map((n) => (
              <li key={n.id} className="text-sm" data-testid="am-note">
                <span className="font-medium">{nameOf(n.accountId)}</span>{' '}
                <ChannelChip channel={n.channel} />
                <span className="block whitespace-pre-wrap text-xs text-muted-foreground">
                  {n.body}
                  <span className="ml-1 opacity-70">
                    {n.authorName}, {relativeTime(n.createdAt, Date.now())}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── 4 · Recently added by your clients ───────────────────────────────────

export function durationLabel(ms: number | null): string {
  if (ms === null) return '';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function AssetsGallery({ data, amUid, nowMs }: { data: AmAssets; amUid: string | null; nowMs: number }): React.JSX.Element {
  if (data.tiles.length === 0) return <Empty>Nothing uploaded by your clients yet.</Empty>;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="asset-gallery">
        {data.tiles.map((t) => (
          <div key={t.id} data-testid="asset-tile">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded bg-muted">
              {t.thumbUrl ? (
                <img src={t.thumbUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <ImageOff className="h-6 w-6" aria-hidden />
                </div>
              )}
              {t.isNew && (
                <span className="absolute left-1 top-1 rounded bg-kyck-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  New
                </span>
              )}
              {t.mediaType === 'video' && (
                <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                  <Play className="h-3 w-3" aria-hidden /> {durationLabel(t.durationMs)}
                </span>
              )}
            </div>
            <Link to={`/accounts/${t.accountId}?tab=assets`} className="mt-1 block truncate text-xs font-medium hover:underline">
              {t.clubName}
            </Link>
            <span className="block text-[11px] text-muted-foreground">{relativeTime(t.createdAt, nowMs)}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 text-right">
        <Link to={viewAllAssetsHref(amUid)} className="text-xs underline" data-testid="assets-view-all">
          View all
        </Link>
      </div>
    </div>
  );
}

// ── 5 · Accounts at a glance ─────────────────────────────────────────────

const paceDot: Record<PaceStatus, string> = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500' };

function PaceCell({ p }: { p: GlanceRow['metaPace'] }): React.JSX.Element {
  if (!p || p.pace === null) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('h-2 w-2 rounded-full', p.status ? paceDot[p.status] : 'bg-slate-300')} />
      {pct(p.pace)}
    </span>
  );
}

export function GlanceTable({ rows }: { rows: GlanceRow[] }): React.JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 5);
  if (rows.length === 0) return <Empty>No active accounts in this view.</Empty>;
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Account</th>
              <th className="px-2">Health</th>
              <th className="px-2">Connections</th>
              <th className="px-2">Meta pace</th>
              <th className="px-2">Google pace</th>
              <th className="px-2 text-right">Spend 7d</th>
              <th className="px-2 text-right">Leads 7d</th>
              <th className="px-2 text-right">CPL 7d</th>
              <th className="px-2 text-right">Alerts</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.accountId} className="border-t border-border" data-testid="glance-row">
                <td className="py-1.5 pr-3 font-medium">
                  <Link to={`/accounts/${r.accountId}`} className="hover:underline">{r.name}</Link>
                </td>
                <td className="px-2">
                  <span className={cn('rounded px-1.5 py-0.5 text-xs', r.health === 'critical' ? 'bg-red-100 text-red-800' : r.health === 'attention' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')}>
                    {r.health === 'critical' ? 'Critical' : r.health === 'attention' ? 'Attention' : 'On track'}
                  </span>
                </td>
                <td className={cn('px-2 text-xs', r.connection.ok ? 'text-muted-foreground' : 'text-red-700')}>{r.connection.text}</td>
                <td className="px-2"><PaceCell p={r.metaPace} /></td>
                <td className="px-2"><PaceCell p={r.googlePace} /></td>
                <td className="px-2 text-right tabular-nums">{money(r.spend7, r.currency)}</td>
                <td className="px-2 text-right tabular-nums">
                  {Math.round(r.leads7)}
                  <span className="ml-1 text-xs text-muted-foreground">
                    {r.leadsPrev7 > 0 ? `${r.leads7 >= r.leadsPrev7 ? '+' : ''}${Math.round(((r.leads7 - r.leadsPrev7) / r.leadsPrev7) * 100)}%` : ''}
                  </span>
                </td>
                <td className="px-2 text-right tabular-nums">
                  {r.verdict.kind === 'no_leads' ? (
                    <NoLeads spend={r.verdict.spend} currency={r.currency} />
                  ) : (
                    cplCellText(r.verdict, (n) => money(n, r.currency))
                  )}
                </td>
                <td className="px-2 text-right tabular-nums">{r.openAlerts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && (
        <button type="button" className="mt-2 text-xs underline" onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

// ── 6 · Campaigns needing attention + leads and client portal ────────────

export type CampTab = 'ending' | 'paused' | 'rejected' | 'waiting';

const CAMP_TABS: Array<{ key: CampTab; label: string; pick: (c: AmCampaigns) => AmCampaignItem[] }> = [
  { key: 'ending', label: 'Ending soon', pick: (c) => c.endingSoon },
  { key: 'paused', label: 'Paused unexpectedly', pick: (c) => c.pausedUnexpectedly },
  { key: 'rejected', label: 'Rejected ads', pick: (c) => c.rejectedAds },
  { key: 'waiting', label: 'Waiting to publish', pick: (c) => c.waitingToPublish },
];

export function CampaignsTabs({ data, tab, onTab }: { data: AmCampaigns; tab: CampTab; onTab: (t: CampTab) => void }): React.JSX.Element {
  const active = CAMP_TABS.find((t) => t.key === tab) ?? CAMP_TABS[0]!;
  const items = active.pick(data);
  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1" role="tablist">
        {CAMP_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab}
            onClick={() => onTab(t.key)}
            className={cn('rounded-full border px-2.5 py-0.5 text-xs', t.key === tab ? 'border-foreground bg-foreground text-background' : 'border-border')}
          >
            {t.label} <span className="tabular-nums">{t.pick(data).length}</span>
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : (
        <ul className="scroll-subtle max-h-60 space-y-1.5 overflow-y-auto pr-1">
          {items.map((c, i) => (
            <li key={`${c.accountId}${c.campaignId ?? i}`} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">
                <span className="font-medium">{c.accountName}</span>
                <span className="text-muted-foreground"> · {c.name ?? 'Campaign'}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-2 text-xs">
                <span className="text-muted-foreground">{c.detail}</span>
                {c.noReplacement && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">No replacement drafted</span>}
                <Link to={c.href} className="underline">Open</Link>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Ending soon covers campaigns published through the Ad Builder. Paused means paused after spending in the last 3 days.
      </p>
    </div>
  );
}

export function PeopleBlock({ data }: { data: AmPeople }): React.JSX.Element {
  return (
    <div className="space-y-3 text-sm" data-testid="people-block">
      <div>
        <p className="font-medium">
          KyckConnect leads needing a person: <span className="tabular-nums">{data.needsPerson.count}</span>
        </p>
        <ul className="text-xs text-muted-foreground">
          {data.needsPerson.top.map((l) => (
            <li key={l.leadId}>{l.clubName}, {relativeTime(l.receivedAt, Date.now())}</li>
          ))}
        </ul>
      </div>
      <p>Unassigned relay leads (agency-wide): <span className="tabular-nums">{data.unassignedRelay.count}</span></p>
      <div>
        <p className="font-medium">Failed CRM deliveries (14 days)</p>
        {data.failedDeliveries.length === 0 ? (
          <p className="text-xs text-muted-foreground">None.</p>
        ) : (
          <ul className="text-xs">
            {data.failedDeliveries.map((f) => (
              <li key={f.accountId}>{f.clubName}: <span className="text-red-700">{f.failed} failed</span></li>
            ))}
          </ul>
        )}
      </div>
      {data.tasksDue !== null && (
        <div>
          <p className="font-medium">My tasks due this week</p>
          {data.tasksDue.length === 0 ? (
            <p className="text-xs text-muted-foreground">None.</p>
          ) : (
            <ul className="text-xs">
              {data.tasksDue.map((t) => (
                <li key={t.taskId}><Link to={`/plans/${t.planId}`} className="underline">{t.title}</Link> · {t.dueDate}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── 7 · High CPL ─────────────────────────────────────────────────────────

export function HighCplTable({ rows }: { rows: HighCplRow[] }): React.JSX.Element {
  if (rows.length === 0) return <Empty>No accounts are over 1.3 times their brand average.</Empty>;
  return (
    <div className="scroll-subtle max-h-72 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card text-left text-xs text-muted-foreground">
          <tr><th className="py-1 pr-3">Account</th><th className="px-2 text-right">Spend</th><th className="px-2 text-right">Leads</th><th className="px-2 text-right">CPL</th><th className="px-2 text-right">Brand avg</th><th className="px-2 text-right">Over</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.accountId} className="border-t border-border" data-testid="high-cpl-row">
              <td className="py-1.5 pr-3 font-medium"><Link to={`/accounts/${r.accountId}`} className="hover:underline">{r.name}</Link></td>
              <td className="px-2 text-right tabular-nums">{money(r.spend, r.currency)}</td>
              <td className="px-2 text-right tabular-nums">{Math.round(r.leads)}</td>
              <td className="px-2 text-right tabular-nums">{r.verdict.kind === 'cpl' ? money(r.verdict.cpl, r.currency) : <NoLeads spend={r.spend} currency={r.currency} />}</td>
              <td className="px-2 text-right tabular-nums">{r.brandCpl === null ? '-' : money(r.brandCpl, r.currency)}</td>
              <td className="px-2 text-right tabular-nums">{r.over === null ? <span className="font-medium text-red-700">No leads</span> : `+${Math.round(r.over * 100)}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── 8 · Movers ───────────────────────────────────────────────────────────

function MoverTable({ title, rows, tone }: { title: string; rows: MoverRow[]; tone: 'good' | 'bad' }): React.JSX.Element {
  return (
    <div className="min-w-0">
      <h3 className="mb-1 flex items-center gap-2 text-xs font-medium">
        {title}
        <span className="rounded-full bg-muted px-2 py-0.5 tabular-nums">{rows.length}</span>
      </h3>
      {rows.length === 0 ? (
        <Empty>None.</Empty>
      ) : (
        <div className="scroll-subtle max-h-56 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card text-left text-muted-foreground">
              <tr><th className="py-1 pr-2">Account</th><th className="px-1 text-right">Leads</th><th className="px-1 text-right">CPL before</th><th className="px-1 text-right">CPL now</th><th className="px-1 text-right">Change</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.accountId} className="border-t border-border">
                  <td className="py-1 pr-2 font-medium"><Link to={`/accounts/${r.accountId}`} className="hover:underline">{r.name}</Link></td>
                  <td className="px-1 text-right tabular-nums">{Math.round(r.leadsBefore)} {'->'} {Math.round(r.leadsAfter)}</td>
                  <td className="px-1 text-right tabular-nums">{r.cplBefore === null ? '-' : money(r.cplBefore, r.currency)}</td>
                  <td className="px-1 text-right tabular-nums">{r.cplNow === null ? '-' : money(r.cplNow, r.currency)}</td>
                  <td className={cn('px-1 text-right font-medium tabular-nums', tone === 'good' ? 'text-green-700' : 'text-red-700')}>
                    {r.kind === 'leads_gone' ? 'No leads' : r.kind === 'leads_back' ? 'Leads back' : `${(r.change ?? 0) > 0 ? '+' : ''}${Math.round((r.change ?? 0) * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MoversGrid({ meta, google }: { meta: MoversResult; google: MoversResult }): React.JSX.Element {
  return (
    <div>
      <div className="grid gap-4 lg:grid-cols-2">
        <MoverTable title="Positive · Meta" rows={meta.positive} tone="good" />
        <MoverTable title="Negative · Meta" rows={meta.negative} tone="bad" />
        <MoverTable title="Positive · Google" rows={google.positive} tone="good" />
        <MoverTable title="Negative · Google" rows={google.negative} tone="bad" />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="movers-note">
        CPL for the past 7 days against the 7 before. Accounts with fewer than 5 leads in both weeks are left out unless their leads went to zero or came back
        ({meta.leftOut + google.leftOut} left out).
      </p>
    </div>
  );
}

// ── 9 · Top 10 Meta ads by cost per lead ─────────────────────────────────

export function TopAdsGrid({ data }: { data: AmTopAds }): React.JSX.Element {
  if (data.ads.length === 0) return <Empty>No ads with at least 5 leads in this period.</Empty>;
  const cpl = (spend: number, leads: number, c: string): string => (leads > 0 ? money(spend / leads, c) : '-');
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="top-ads">
      {data.ads.map((a) => (
        <div key={a.adId} className="min-w-0 text-xs" data-testid="top-ad">
          <div className="aspect-[4/5] w-full overflow-hidden rounded bg-muted">
            {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt={a.adName ?? ''} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-6 w-6" aria-hidden /></div>}
          </div>
          <Link to={`/accounts/${a.accountId}`} className="mt-1 block truncate font-medium hover:underline" title={a.adName ?? a.adId}>{a.adName ?? a.adId}</Link>
          <span className="block truncate text-muted-foreground">{a.accountName ?? a.accountId}</span>
          <dl className="mt-1 grid grid-cols-2 gap-x-2 tabular-nums">
            <dt className="text-muted-foreground">On-Meta leads</dt><dd className="text-right">{a.metaLeads > 0 ? Math.round(a.metaLeads) : '-'}</dd>
            <dt className="text-muted-foreground">On-Meta CPL</dt><dd className="text-right">{a.metaLeads > 0 ? cpl(a.spend, a.metaLeads, a.currency) : '-'}</dd>
            <dt className="text-muted-foreground">Website leads</dt><dd className="text-right">{a.websiteLeads > 0 ? Math.round(a.websiteLeads) : '-'}</dd>
            <dt className="text-muted-foreground">Website CPL</dt><dd className="text-right">{a.websiteLeads > 0 ? cpl(a.spend, a.websiteLeads, a.currency) : '-'}</dd>
            <dt className="text-muted-foreground">Spend</dt><dd className="text-right">{money(a.spend, a.currency)}</dd>
            <dt className="font-medium">Total CPL</dt><dd className="text-right font-medium">{cpl(a.spend, a.leads, a.currency)}</dd>
          </dl>
        </div>
      ))}
    </div>
  );
}

// ── 10 · Budget pacing ───────────────────────────────────────────────────

export const PACE_PERIODS: Array<{ key: PacePeriod; label: string }> = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'past_7', label: 'Past 7 days' },
  { key: 'past_30', label: 'Past 30 days' },
];
export const PACE_TABS: Array<{ key: PaceTab; label: string }> = [
  { key: 'combined', label: 'Combined' },
  { key: 'meta', label: 'Meta' },
  { key: 'google', label: 'Google' },
];

const barTone: Record<PaceStatus, string> = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500' };

export function PaceSection({
  result,
  period,
  tab,
  onPeriod,
  onTab,
}: {
  result: PaceResult;
  period: PacePeriod;
  tab: PaceTab;
  onPeriod: (p: PacePeriod) => void;
  onTab: (t: PaceTab) => void;
}): React.JSX.Element {
  const chip = (active: boolean): string => cn('rounded-full border px-2.5 py-0.5 text-xs', active ? 'border-foreground bg-foreground text-background' : 'border-border');
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {PACE_PERIODS.map((p) => (
            <button key={p.key} type="button" className={chip(p.key === period)} onClick={() => onPeriod(p.key)}>{p.label}</button>
          ))}
        </div>
        <div className="flex gap-1" role="tablist">
          {PACE_TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={t.key === tab} className={chip(t.key === tab)} onClick={() => onTab(t.key)}>{t.label}</button>
          ))}
        </div>
      </div>
      <div className="mb-3 flex flex-wrap gap-4" data-testid="pace-totals">
        {result.totals.map((t) => (
          <div key={t.currency} className="text-sm">
            <span className="font-medium">{t.currency}</span>: spent {money(t.spent, t.currency)}, expected {money(t.expected, t.currency)}, pace {pct(t.pace)}
            {t.projected !== null && <>, projected {money(t.projected, t.currency)}</>}
          </div>
        ))}
        {result.noBudget.length > 0 && <span className="text-xs text-muted-foreground">{result.noBudget.length} with no budget set (excluded)</span>}
      </div>
      <ul className="scroll-subtle max-h-80 space-y-2 overflow-y-auto pr-1">
        {result.rows.map((r) => {
          const max = Math.max(r.spent, r.expected, 1);
          return (
            <li key={r.accountId} className="text-xs" data-testid="pace-row">
              <div className="flex justify-between"><Link to={`/accounts/${r.accountId}`} className="font-medium hover:underline">{r.name}</Link><span className="tabular-nums">{money(r.spent, r.currency)} of {money(r.expected, r.currency)} · {pct(r.pace)}</span></div>
              <div className="relative mt-1 h-2 rounded bg-muted">
                <div className={cn('h-2 rounded', r.status ? barTone[r.status] : 'bg-slate-300')} style={{ width: `${Math.min(100, (r.spent / max) * 100)}%` }} />
                <div className="absolute top-[-2px] h-3 w-0.5 bg-foreground" style={{ left: `${Math.min(100, (r.expected / max) * 100)}%` }} title="Expected by today" />
              </div>
            </li>
          );
        })}
        {result.noBudget.map((n) => (
          <li key={n.accountId} className="flex justify-between text-xs text-muted-foreground"><span>{n.name}</span><span>No budget set</span></li>
        ))}
      </ul>
    </div>
  );
}

